import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Play,
  Plus,
  X,
  Loader2,
  Save,
  Trash2,
  Code,
  FileCode,
  ShieldCheck,
  Sparkles,
  Wand2,
  Info,
  ChevronDown,
  MessageSquare,
  Bug,
  Compass,
  Download,
  Eye,
  RefreshCw,
  Globe,
  Settings2,
  Layers
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useStarterPlan } from "../contexts/OrganizationPlanContext";

const API_BASE = "https://api.testoraglobal.in/api/api-testing";

export default function Api_testing({ darkMode }) {
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const projectRole = selectedProject?.user_role;
  const canExecuteRequests =
    starterBypass || (currentUser?.role !== "org_admin" && projectRole === "qa");
  const canManageCollections =
    starterBypass ||
    (currentUser?.role !== "org_admin" &&
    ["qa", "developer"].includes(projectRole));
  const isProjectReadOnly =
    !starterBypass &&
    (currentUser?.role === "org_admin" || projectRole === "manager");

  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";
  const borderColor = darkMode ? "border-gray-800/50" : "border-gray-200";
  const textColor = darkMode ? "text-gray-100" : "text-gray-900";
  const textMuted = darkMode ? "text-gray-400" : "text-gray-500";
  const inputBg = darkMode ? "bg-[#0b0d14]" : "bg-gray-50";
  const hoverBg = darkMode ? "hover:bg-white/5" : "hover:bg-gray-100";

  // ---------- Environment State (Postman Parity) ----------
  const [environments, setEnvironments] = useState([
    {
      id: "env-1",
      name: "Development",
      variables: [
        { key: "baseUrl", value: "http://localhost:3000" },
        { key: "token", value: "dev_token_123" },
        { key: "", value: "" }
      ]
    },
    {
      id: "env-2",
      name: "Production",
      variables: [
        { key: "baseUrl", value: "https://api.testora.com" },
        { key: "token", value: "prod_token_999" },
        { key: "", value: "" }
      ]
    }
  ]);
  const [activeEnvironmentId, setActiveEnvironmentId] = useState("none");
  const [showEnvManager, setShowEnvManager] = useState(false);
  const [editingEnvId, setEditingEnvId] = useState(null);

  // ---------- Tab State ----------
  const createNewTab = (id) => ({
    id,
    name: "Untitled Request",
    method: "GET",
    url: "{{baseUrl}}/api/endpoint",
    bodyType: "raw",
    rawBodyType: "JSON",
    body: "",
    formData: [{ key: "", value: "" }],
    urlEncoded: [{ key: "", value: "" }],
    graphqlQuery: "",
    binaryFile: null,
    params: [{ key: "", value: "" }],
    headers: [{ key: "", value: "" }],
    testScript:
      "// Postman-style assertion scripts\npm.test('Status code is 200', function () {\n    pm.response.to.have.status(200);\n});",
    isAILoading: false,
    aiProposedTests: [],
    auth: {
      type: "none",
      token: "",
      username: "",
      password: "",
      apiKeyName: "api_key",
      apiKeyValue: "",
      apiKeyLocation: "header",
      digestUsername: "",
      digestPassword: "",
      digestRealm: "",
      digestNonce: "",
      oauth1ConsumerKey: "",
      oauth1ConsumerSecret: "",
      oauth1Token: "",
      oauth1TokenSecret: "",
      oauth2Token: "",
      oauth2GrantType: "client_credentials",
      oauth2TokenUrl: "",
      oauth2ClientId: "",
      oauth2ClientSecret: "",
      awsAccessKey: "",
      awsSecretKey: "",
      awsRegion: "us-east-1",
      awsService: "execute-api",
    },
    response: null,
    isLoading: false,
    visualizerTemplate: null,
    visualExplanation: "",
    visualHtml: null,
    isVisualLoading: false,
  });

  const [tabs, setTabs] = useState([createNewTab(1)]);
  const [activeTabId, setActiveTabId] = useState(1);
  const [activeSubTab, setActiveSubTab] = useState("Params");
  const [responseViewTab, setResponseViewTab] = useState("Pretty");

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savedRequests, setSavedRequests] = useState([]);
  const [saveData, setSaveData] = useState({
    name: "", method: "", url: "", body: "", bodyType: "raw", rawBodyType: "JSON",
    params: [], headers: [], auth: {}, formData: [], urlEncoded: [], graphqlQuery: "", testScript: "",
  });

  // ---------- AI Assistant panel state ----------
  const [sendDropdownOpen, setSendDropdownOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatTitle, setChatTitle] = useState("AI Assistant");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatResult, setChatResult] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);

  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

  const getMethodColor = (method) => {
    switch (method) {
      case "GET": return "text-green-500";
      case "POST": return "text-blue-500";
      case "DELETE": return "text-red-500";
      case "PUT": return "text-yellow-500";
      case "PATCH": return "text-orange-500";
      default: return "text-gray-500";
    }
  };

  const handleAddTab = () => {
    if (isProjectReadOnly) return;
    const newId = Date.now();
    setTabs([...tabs, createNewTab(newId)]);
    setActiveTabId(newId);
  };

  const handleCloseTab = (e, id) => {
    e.stopPropagation();
    if (isProjectReadOnly) return;
    if (tabs.length === 1) return;
    const newTabs = tabs.filter((t) => t.id !== id);
    setTabs(newTabs);
    if (activeTabId === id) setActiveTabId(newTabs[newTabs.length - 1].id);
  };

  const updateActiveTab = (field, value) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.id === activeTabId ? { ...tab, [field]: value } : tab,
      ),
    );
  };

  const updateKeyValueArray = (field, index, keyOrValue, newValue) => {
    const updatedArray = [...activeTab[field]];
    updatedArray[index][keyOrValue] = newValue;
    if (index === updatedArray.length - 1 && newValue !== "") {
      updatedArray.push({ key: "", value: "" });
    }
    updateActiveTab(field, updatedArray);
  };

  const removeTemplateRow = (field, index) => {
    const updatedArray = activeTab[field].filter((_, i) => i !== index);
    if (updatedArray.length === 0) updatedArray.push({ key: "", value: "" });
    updateActiveTab(field, updatedArray);
  };

  const mergeKeyValueArrays = (existing, patchItems) => {
    const cleaned = existing.filter((kv) => kv.key.trim() !== "");
    (patchItems || []).forEach((item) => {
      if (!item || !item.key) return;
      const idx = cleaned.findIndex((kv) => kv.key.toLowerCase() === item.key.toLowerCase());
      if (idx >= 0) cleaned[idx] = { key: item.key, value: item.value ?? "" };
      else cleaned.push({ key: item.key, value: item.value ?? "" });
    });
    cleaned.push({ key: "", value: "" });
    return cleaned;
  };

  // ---------- Environment Functions ----------
  const processEnvString = (str) => {
    if (typeof str !== 'string') return str;
    if (!activeEnvironmentId || activeEnvironmentId === 'none') return str;
    const env = environments.find(e => e.id === activeEnvironmentId);
    if (!env) return str;
    
    return str.replace(/{{(.*?)}}/g, (match, p1) => {
      const key = p1.trim();
      const variable = env.variables.find(v => v.key === key);
      return variable && variable.value !== "" ? variable.value : match;
    });
  };

  const updateEnvKeyValue = (envId, index, keyOrValue, newValue) => {
    if (isProjectReadOnly) return;
    setEnvironments(envs => envs.map(env => {
      if (env.id !== envId) return env;
      const updatedVariables = [...env.variables];
      updatedVariables[index][keyOrValue] = newValue;
      if (index === updatedVariables.length - 1 && newValue !== "") {
        updatedVariables.push({ key: "", value: "" });
      }
      return { ...env, variables: updatedVariables };
    }));
  };

  const removeEnvRow = (envId, index) => {
    if (isProjectReadOnly) return;
    setEnvironments(envs => envs.map(env => {
      if (env.id !== envId) return env;
      const updatedVariables = env.variables.filter((_, i) => i !== index);
      if (updatedVariables.length === 0) updatedVariables.push({ key: "", value: "" });
      return { ...env, variables: updatedVariables };
    }));
  };

  const handleCreateEnvironment = () => {
    if (isProjectReadOnly) return;
    const newEnv = {
      id: `env-${Date.now()}`,
      name: "New Environment",
      variables: [{ key: "", value: "" }]
    };
    setEnvironments([...environments, newEnv]);
    setEditingEnvId(newEnv.id);
  };

  const handleDeleteEnvironment = (id) => {
    if (isProjectReadOnly) return;
    const newEnvs = environments.filter(e => e.id !== id);
    setEnvironments(newEnvs);
    if (activeEnvironmentId === id) setActiveEnvironmentId('none');
    if (editingEnvId === id) setEditingEnvId(newEnvs.length > 0 ? newEnvs[0].id : null);
  };

  // ---------- Send Request Engine ----------
  const handleSendRequest = async (overrideTab) => {
    if (!canExecuteRequests) {
      toast.error("Only QA can send or execute API requests.");
      return;
    }
    const tabData = overrideTab || activeTab;
    if (!tabData.url) return;
    updateActiveTab("isLoading", true);
    updateActiveTab("response", null);
    updateActiveTab("visualHtml", null);
    setResponseViewTab("Pretty");

    const startTime = performance.now();

    try {
      let finalUrl = processEnvString(tabData.url);
      let urlObj;
      try {
        urlObj = new URL(finalUrl);
        tabData.params.forEach((p) => {
          if (p.key.trim())
            urlObj.searchParams.append(processEnvString(p.key.trim()), processEnvString(p.value.trim()));
        });
        if (tabData.auth.type === "apikey" && tabData.auth.apiKeyName && tabData.auth.apiKeyLocation === "query") {
          urlObj.searchParams.append(processEnvString(tabData.auth.apiKeyName.trim()), processEnvString(tabData.auth.apiKeyValue.trim()));
        }
        finalUrl = urlObj.toString();
      } catch (e) {}

      const requestHeaders = {};
      tabData.headers.forEach((h) => {
        if (h.key.trim()) requestHeaders[processEnvString(h.key.trim())] = processEnvString(h.value.trim());
      });

      if (tabData.auth.type === "apikey" && tabData.auth.apiKeyName && tabData.auth.apiKeyLocation === "header") {
        requestHeaders[processEnvString(tabData.auth.apiKeyName.trim())] = processEnvString(tabData.auth.apiKeyValue.trim());
      } else if (tabData.auth.type === "bearer" && tabData.auth.token) {
        requestHeaders["Authorization"] = `Bearer ${processEnvString(tabData.auth.token)}`;
      } else if (tabData.auth.type === "basic" && (tabData.auth.username || tabData.auth.password)) {
        requestHeaders["Authorization"] = `Basic ${btoa(`${processEnvString(tabData.auth.username)}:${processEnvString(tabData.auth.password)}`)}`;
      } else if (tabData.auth.type === "oauth2" && tabData.auth.oauth2Token) {
        requestHeaders["Authorization"] = `Bearer ${processEnvString(tabData.auth.oauth2Token)}`;
      }

      const options = { method: tabData.method, headers: requestHeaders };

      if (["POST", "PUT", "PATCH", "DELETE"].includes(tabData.method)) {
        if (tabData.bodyType === "raw" && tabData.body) {
          let contentType = "application/json";
          if (tabData.rawBodyType === "Text") contentType = "text/plain";
          else if (tabData.rawBodyType === "JavaScript") contentType = "application/javascript";
          else if (tabData.rawBodyType === "HTML") contentType = "text/html";
          else if (tabData.rawBodyType === "XML") contentType = "application/xml";
          options.headers["Content-Type"] = requestHeaders["Content-Type"] || contentType;
          options.body = processEnvString(tabData.body);
        } else if (tabData.bodyType === "form-data") {
          delete options.headers["Content-Type"];
          const formData = new FormData();
          tabData.formData.forEach((item) => {
            if (item.key.trim()) formData.append(processEnvString(item.key.trim()), processEnvString(item.value));
          });
          options.body = formData;
        } else if (tabData.bodyType === "x-www-form-urlencoded") {
          options.headers["Content-Type"] = "application/x-www-form-urlencoded";
          const urlEncoded = new URLSearchParams();
          tabData.urlEncoded.forEach((item) => {
            if (item.key.trim()) urlEncoded.append(processEnvString(item.key.trim()), processEnvString(item.value));
          });
          options.body = urlEncoded;
        } else if (tabData.bodyType === "GraphQL" && tabData.graphqlQuery) {
          options.headers["Content-Type"] = "application/json";
          options.body = JSON.stringify({ query: processEnvString(tabData.graphqlQuery) });
        } else if (tabData.bodyType === "binary" && tabData.binaryFile) {
          options.headers["Content-Type"] = tabData.binaryFile.type || "application/octet-stream";
          options.body = tabData.binaryFile;
        }
      }

      const res = await fetch(finalUrl, options);
      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);
      const responseText = await res.text();

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch {
        responseData = responseText;
      }

      const resHeadersArray = [];
      res.headers.forEach((val, key) => {
        resHeadersArray.push({ key, value: val });
      });

      const executedTestResults = [];
      if (tabData.testScript && tabData.testScript.trim() !== "") {
        try {
          const pm = {
            response: {
              to: {
                have: {
                  status: (code) => {
                    if (res.status !== code) throw new Error(`Expected status ${code} but got ${res.status}`);
                  },
                },
              },
              json: () => responseData,
              responseTime: responseTime,
            },
            test: (name, fn) => {
              try {
                fn();
                executedTestResults.push({ name, status: "PASS" });
              } catch (err) {
                executedTestResults.push({ name, status: "FAIL", error: err.message });
              }
            },
            expect: (initialVal) => {
              let negate = false;
              let val = initialVal;
              const assert = (condition, msg) => {
                if (negate ? condition : !condition)
                  throw new Error(negate ? msg.replace("Expected", "Expected not") : msg);
              };
              const chain = {
                get to() { return chain; }, get be() { return chain; }, get have() { return chain; },
                get and() { return chain; }, get all() { return chain; }, get at() { return chain; },
                get of() { return chain; }, get deep() { return chain; },
                get not() { negate = !negate; return chain; },
                get length() { val = val !== undefined && val !== null ? val.length : undefined; return chain; },
                get exist() { assert(val !== undefined && val !== null, "Expected value to exist"); return chain; },
                get empty() {
                  if (typeof val === "string" || Array.isArray(val)) assert(val.length === 0, "Expected to be empty");
                  else if (typeof val === "object" && val !== null) assert(Object.keys(val).length === 0, "Expected to be empty");
                  return chain;
                },
                eql: (expected) => { assert(val === expected || JSON.stringify(val) === JSON.stringify(expected), `Expected ${expected} but got ${val}`); return chain; },
                equal: (expected) => { assert(val === expected, `Expected ${expected} but got ${val}`); return chain; },
                a: (type) => {
                  if (type === "array") assert(Array.isArray(val), `Expected array`);
                  else assert(typeof val === type, `Expected ${type}`); return chain;
                },
                an: (type) => chain.a(type),
                below: (num) => { assert(val < num, `Expected below ${num}`); return chain; },
                above: (num) => { assert(val > num, `Expected above ${num}`); return chain; },
                least: (num) => { assert(val >= num, `Expected at least ${num}`); return chain; },
                most: (num) => { assert(val <= num, `Expected at most ${num}`); return chain; },
                match: (regex) => { assert(regex.test(val), `Expected value to match regex ${regex}`); return chain; },
                property: (prop) => { assert(val && prop in val, `Expected property '${prop}' missing`); return chain; },
                status: (code) => { assert(res.status === code, `Expected status ${code}`); return chain; },
                include: (item) => { assert(val && typeof val.includes === "function" && val.includes(item), `Expected to include ${item}`); return chain; },
                includes: (item) => chain.include(item), contain: (item) => chain.include(item),
                keys: (...keys) => {
                  const flatKeys = Array.isArray(keys[0]) ? keys[0] : keys;
                  assert(val, `Cannot check keys of undefined object`);
                  const valKeys = Object.keys(val);
                  for (let k of flatKeys) assert(valKeys.includes(k), `Missing key '${k}'`);
                  return chain;
                },
                lengthOf: (len) => { assert(val && val.length === len, `Expected length ${len}`); return chain; },
              };
              return chain;
            },
          };

          const runTests = new Function("pm", tabData.testScript);
          runTests(pm);
        } catch (executionError) {
          executedTestResults.push({ name: "Script Syntax Error", status: "FAIL", error: executionError.message });
        }
      }

      const sizeInBytes = new Blob([responseText]).size;
      updateActiveTab("response", {
        status: res.status,
        statusText: res.statusText || (res.ok ? "OK" : "Error"),
        time: responseTime + " ms",
        size: sizeInBytes > 1024 ? (sizeInBytes / 1024).toFixed(2) + " KB" : sizeInBytes + " B",
        body: responseData,
        rawBody: responseText,
        headers: resHeadersArray,
        testResults: executedTestResults,
      });

      if (tabData.visualizerTemplate) {
        updateActiveTab("visualHtml", buildVisualizerDoc(tabData.visualizerTemplate, responseData));
      }
    } catch (error) {
      updateActiveTab("response", {
        status: "ERR",
        statusText: "Network Error",
        time: "0 ms",
        size: "0 B",
        body: { error: error.message, hint: "Check CORS policy or endpoint availability." },
        rawBody: JSON.stringify({ error: error.message }),
        headers: [],
        testResults: [{ name: "Request Failed", status: "FAIL", error: error.message }],
      });
    } finally {
      updateActiveTab("isLoading", false);
    }
  };

  const buildVisualizerDoc = (template, data) => {
    const safeTemplateJSON = JSON.stringify(template || "");
    const safeDataJSON = JSON.stringify(data === undefined ? null : data);
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<script src="https://cdnjs.cloudflare.com/ajax/libs/handlebars.js/4.7.8/handlebars.min.js"><\/script>
<style>
  html, body { margin: 0; padding: 16px; background: #ffffff; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1f2937; }
  #testora-visualizer-root { max-width: 100%; overflow-x: auto; }
  #testora-visualizer-error { color: #b91c1c; font-family: monospace; white-space: pre-wrap; padding: 12px; border: 1px solid #fecaca; background: #fef2f2; border-radius: 8px; font-size: 12px; }
  table { border-collapse: collapse; width: 100%; }
</style>
</head>
<body>
<div id="testora-visualizer-root">Loading visualization&hellip;</div>
<script>
  (function () {
    try {
      var tpl = Handlebars.compile(${safeTemplateJSON});
      var html = tpl({ response: ${safeDataJSON} });
      document.getElementById('testora-visualizer-root').innerHTML = html;
    } catch (err) {
      document.getElementById('testora-visualizer-root').innerHTML =
        '<div id="testora-visualizer-error">Visualizer template error: ' +
        (err && err.message ? String(err.message).replace(/</g, '&lt;') : 'unknown error') +
        '</div>';
    }
  })();
<\/script>
</body>
</html>`;
  };

  const applyVisualizerTemplate = (template) => {
    updateActiveTab("visualizerTemplate", template);
    if (activeTab.response) {
      let parsed;
      try {
        parsed = JSON.parse(activeTab.response.rawBody);
      } catch {
        parsed = activeTab.response.rawBody;
      }
      updateActiveTab("visualHtml", buildVisualizerDoc(template, parsed));
    }
  };

  const handleGenerateVisualization = async () => {
    if (!activeTab.response) return toast.error("Please send the request first.");
    updateActiveTab("isVisualLoading", true);
    try {
      const { data } = await axios.post(`${API_BASE}/ai/visualize-response`, {
        url: activeTab.url,
        method: activeTab.method,
        responseBody: activeTab.response.rawBody,
      });
      if (data.template) {
        updateActiveTab("visualExplanation", data.explanation || "");
        applyVisualizerTemplate(data.template);
      } else toast.error(data.error || "Failed to generate visualization.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Connection error generating the visualization.");
    }
    finally { updateActiveTab("isVisualLoading", false); }
  };

  const handleVisualizeTabClick = () => {
    setResponseViewTab("Visualize");
    if (!activeTab.response) return;
    if (activeTab.visualizerTemplate && !activeTab.visualHtml) applyVisualizerTemplate(activeTab.visualizerTemplate);
    else if (!activeTab.visualizerTemplate && !activeTab.visualHtml) handleGenerateVisualization();
  };

  const handleAIGenerateTests = async () => {
    if (!activeTab.response) return toast.error("Please send the request first.");
    updateActiveTab("isAILoading", true);
    try {
      const { data } = await axios.post(`${API_BASE}/ai/generate-tests`, {
        responseBody: activeTab.response.rawBody,
        method: activeTab.method,
        url: activeTab.url,
        status: activeTab.response.status,
      });
      if (data.generatedTests && Array.isArray(data.generatedTests)) updateActiveTab("aiProposedTests", data.generatedTests);
      else toast.error(data.error || "Failed to retrieve formatted tests.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Connection error generating tests.");
    }
    finally { updateActiveTab("isAILoading", false); }
  };

  const handleAIGenerateMockBody = async () => {
    updateActiveTab("isAILoading", true);
    try {
      const { data } = await axios.post(`${API_BASE}/ai/generate-mock-body`, {
        url: activeTab.url,
        method: activeTab.method,
      });
      if (data.mockPayload) {
        updateActiveTab("bodyType", "raw");
        updateActiveTab("rawBodyType", "JSON");
        updateActiveTab("body", JSON.stringify(data.mockPayload, null, 2));
      } else toast.error(data.error || "Failed to generate a mock payload.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Connection error generating the mock payload.");
    }
    finally { updateActiveTab("isAILoading", false); }
  };

  const handleAIAssistant = async (action) => {
    setSendDropdownOpen(false);
    if (!activeTab.response) return toast.error("Please send the request first.");
    setChatTitle(action === "success" ? "Achieving Successful Responses" : action === "debug" ? "Debugging Request" : "Exploring API Capabilities");
    setIsChatOpen(true); setIsChatLoading(true); setChatResult(null);

    try {
      const payload = {
        action,
        request: { url: activeTab.url, method: activeTab.method, headers: activeTab.headers.filter(h => h.key), params: activeTab.params.filter(p => p.key), body: activeTab.body },
        response: { status: activeTab.response.status, time: activeTab.response.time, body: activeTab.response.rawBody },
      };
      const { data } = await axios.post(`${API_BASE}/ai/assistant`, payload);
      if (data.error) setChatResult({ analysis: data.error, proposedType: null, suggestions: [] });
      else setChatResult({ analysis: data.analysis || "No analysis available.", proposedType: data.proposedType || null, proposedTemplate: data.proposedTemplate || null, proposedPatch: data.proposedPatch || null, suggestions: data.suggestions || [] });
    } catch (err) {
      setChatResult({
        analysis: err.response?.data?.error || "Connection error communicating with the AI Assistant.",
        proposedType: null,
        suggestions: [],
      });
    }
    finally { setIsChatLoading(false); }
  };

  const handleApproveProposal = () => {
    if (!chatResult) return;
    if (chatResult.proposedType === "visualizer" && chatResult.proposedTemplate) {
      applyVisualizerTemplate(chatResult.proposedTemplate);
      setResponseViewTab("Visualize"); setIsChatOpen(false); toast.success("Visualization applied.");
    } else if (chatResult.proposedType === "requestPatch" && chatResult.proposedPatch) {
      const patch = chatResult.proposedPatch;
      const newHeaders = patch.headers?.length ? mergeKeyValueArrays(activeTab.headers, patch.headers) : activeTab.headers;
      const newParams = patch.params?.length ? mergeKeyValueArrays(activeTab.params, patch.params) : activeTab.params;
      const bodyChanged = patch.body !== undefined && patch.body !== null;
      const newBody = bodyChanged ? patch.body : activeTab.body;
      const newBodyType = bodyChanged ? "raw" : activeTab.bodyType;
      updateActiveTab("headers", newHeaders); updateActiveTab("params", newParams);
      if (bodyChanged) { updateActiveTab("body", newBody); updateActiveTab("bodyType", newBodyType); }
      setIsChatOpen(false); toast.success("Fix applied — resending request...");
      handleSendRequest({ ...activeTab, headers: newHeaders, params: newParams, body: newBody, bodyType: newBodyType });
    }
    setChatResult(null);
  };

  const handleRejectProposal = () => {
    setChatResult((prev) => (prev ? { ...prev, proposedType: null, proposedTemplate: null, proposedPatch: null } : prev));
    toast("Suggestion dismissed.");
  };

  const handleTrySuggestion = (s) => {
    updateActiveTab("method", s.method || "GET"); updateActiveTab("url", s.url || activeTab.url);
    setIsChatOpen(false); toast.success("Loaded into the request bar — hit Send to try it.");
  };

  const handleDownloadResponse = () => {
    setSendDropdownOpen(false);
    if (!activeTab.response || !activeTab.response.rawBody) return toast.error("No response data available to download.");
    const contentTypeHeader = (activeTab.response.headers || []).find((h) => h.key.toLowerCase() === "content-type")?.value || "";
    let ext = "txt", mime = "text/plain";
    if (contentTypeHeader.includes("json") || typeof activeTab.response.body === "object") { ext = "json"; mime = "application/json"; }
    else if (contentTypeHeader.includes("html")) { ext = "html"; mime = "text/html"; }
    else if (contentTypeHeader.includes("xml")) { ext = "xml"; mime = "application/xml"; }

    const blob = new Blob([activeTab.response.rawBody], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${(activeTab.name || "response").replace(/[^a-z0-9]/gi, "_").toLowerCase()}_${Date.now()}.${ext}`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Response downloaded.");
  };

  const fetchSavedRequests = async () => {
    if (!selectedProject?.project_id || !currentUser?.email) return;
    try { 
      const res = await fetch(`${API_BASE}/all`); 
      const data = await res.json();
      
      const filteredData = data.filter((req) => {
        const pid = req.project_id || req.auth?._meta?.project_id;
        const uemail = req.user_email || req.auth?._meta?.user_email;
        return pid === selectedProject.project_id && uemail === currentUser.email;
      });
      setSavedRequests(filteredData); 
    } 
    catch (err) { console.log("Failed to fetch saved queries.", err); }
  };

  useEffect(() => { 
    fetchSavedRequests(); 
  }, [selectedProject?.project_id, currentUser?.email]);

  useEffect(() => {
    if (!isChatOpen) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setIsChatOpen(false); };
    window.addEventListener("keydown", onKeyDown); return () => window.removeEventListener("keydown", onKeyDown);
  }, [isChatOpen]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!canManageCollections) return;
    try {
      const response = await fetch(`${API_BASE}/delete/${id}`, { method: "DELETE" });
      if (response.ok) setSavedRequests((prev) => prev.filter((req) => req._id !== id));
    } catch (error) {}
  };

  const handleOpenSave = () => {
    if (!canManageCollections) return;
    setSaveData({
      name: activeTab.name === "Untitled Request" ? "" : activeTab.name, method: activeTab.method, url: activeTab.url,
      bodyType: activeTab.bodyType, rawBodyType: activeTab.rawBodyType, body: activeTab.body, formData: activeTab.formData,
      urlEncoded: activeTab.urlEncoded, graphqlQuery: activeTab.graphqlQuery, params: activeTab.params, headers: activeTab.headers,
      auth: { 
        ...activeTab.auth, 
        _meta: { project_id: selectedProject?.project_id, user_email: currentUser?.email } 
      }, 
      testScript: activeTab.testScript,
      project_id: selectedProject?.project_id,
      user_email: currentUser?.email
    });
    setShowSaveModal(true);
  };

  const handleSaveRequest = async () => {
    if (!canManageCollections) return;
    try {
      const payload = {
        ...saveData,
        project_id: selectedProject?.project_id,
        user_email: currentUser?.email,
        auth: {
          ...saveData.auth,
          _meta: { project_id: selectedProject?.project_id, user_email: currentUser?.email }
        }
      };
      await fetch(`${API_BASE}/save`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (saveData.name) updateActiveTab("name", saveData.name);
      setShowSaveModal(false); 
      fetchSavedRequests();
    } catch (err) {}
  };

  const renderKeyValueEditor = (field) => (
    <div className="space-y-2">
      <div className="flex gap-2 mb-2 px-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
        <div className="flex-1">Key</div><div className="flex-1">Value</div><div className="w-8"></div>
      </div>
      {activeTab[field].map((item, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input value={item.key} onChange={(e) => updateKeyValueArray(field, i, "key", e.target.value)} placeholder="Key" className={`flex-1 p-2 text-sm border ${borderColor} rounded-lg ${inputBg} focus:ring-1 focus:ring-purple-500 outline-none`} />
          <input value={item.value} onChange={(e) => updateKeyValueArray(field, i, "value", e.target.value)} placeholder="Value" className={`flex-1 p-2 text-sm border ${borderColor} rounded-lg ${inputBg} focus:ring-1 focus:ring-purple-500 outline-none`} />
          <button onClick={() => removeTemplateRow(field, i)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><X size={16} /></button>
        </div>
      ))}
    </div>
  );

  const renderAuthEditor = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-1 border-r border-gray-800/50 pr-4">
        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Auth Type</label>
        <select value={activeTab.auth.type} onChange={(e) => updateActiveTab("auth", { ...activeTab.auth, type: e.target.value })} className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none cursor-pointer text-purple-400 font-bold`}>
          <option value="none">No Auth</option><option value="apikey">API Key</option><option value="bearer">Bearer Token</option>
          <option value="basic">Basic Auth</option><option value="digest">Digest Auth</option><option value="oauth1">OAuth 1.0</option>
          <option value="oauth2">OAuth 2.0</option><option value="aws">AWS Signature</option>
        </select>
      </div>
      <div className="md:col-span-2 space-y-4">
        {activeTab.auth.type === "none" && (<div className="text-sm text-gray-500 flex items-center gap-2 py-4"><ShieldCheck size={18} /> This request does not use any authorization headers.</div>)}
        {activeTab.auth.type === "apikey" && (
          <div className="space-y-4 animate-in fade-in duration-200">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-xs font-bold mb-1.5 text-gray-400">Key Name</label><input type="text" value={activeTab.auth.apiKeyName || ""} onChange={(e) => updateActiveTab("auth", { ...activeTab.auth, apiKeyName: e.target.value })} placeholder="X-API-Key" className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none`} /></div>
              <div><label className="block text-xs font-bold mb-1.5 text-gray-400">Value</label><input type="text" value={activeTab.auth.apiKeyValue || ""} onChange={(e) => updateActiveTab("auth", { ...activeTab.auth, apiKeyValue: e.target.value })} placeholder="Secret value" className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none`} /></div>
            </div>
            <div><label className="block text-xs font-bold mb-1.5 text-gray-400">Add to</label><select value={activeTab.auth.apiKeyLocation || "header"} onChange={(e) => updateActiveTab("auth", { ...activeTab.auth, apiKeyLocation: e.target.value })} className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none`}><option value="header">Header</option><option value="query">Query Params</option></select></div>
          </div>
        )}
        {activeTab.auth.type === "bearer" && (
          <div className="space-y-2 animate-in fade-in duration-200"><label className="block text-xs font-bold text-gray-400">Token</label><input type="text" value={activeTab.auth.token} onChange={(e) => updateActiveTab("auth", { ...activeTab.auth, token: e.target.value })} placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none font-mono text-purple-300`} /></div>
        )}
        {activeTab.auth.type === "basic" && (
          <div className="grid grid-cols-2 gap-4 animate-in fade-in duration-200">
            <div><label className="block text-xs font-bold mb-1.5 text-gray-400">Username</label><input type="text" value={activeTab.auth.username} onChange={(e) => updateActiveTab("auth", { ...activeTab.auth, username: e.target.value })} className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none`} /></div>
            <div><label className="block text-xs font-bold mb-1.5 text-gray-400">Password</label><input type="password" value={activeTab.auth.password} onChange={(e) => updateActiveTab("auth", { ...activeTab.auth, password: e.target.value })} className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none`} /></div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex w-full min-w-0 items-stretch md:flex-row flex-col">
      <aside aria-label="Testora AI Assistant" className={`${cardBg} border-l ${borderColor} shadow-[-12px_0_40px_rgba(0,0,0,0.12)] flex flex-col shrink-0 overflow-hidden transition-[width,opacity] order-2 md:order-2 md:sticky md:top-4 md:h-[calc(100vh-2rem)] duration-300 ease-in-out ${isChatOpen ? "w-full md:w-[420px] opacity-100" : "w-0 opacity-0 pointer-events-none border-l-0"}`}>
        <div className={`p-5 border-b ${borderColor} flex justify-between items-center bg-gradient-to-r from-purple-500/10 to-transparent`}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg"><Sparkles className="h-5 w-5 text-purple-400" /></div>
            <div><h3 className="font-bold text-base tracking-tight text-white">Testora API AI Assistant</h3><p className="text-[11px] font-medium text-purple-300/80 uppercase tracking-widest">{chatTitle}</p></div>
          </div>
          <button onClick={() => setIsChatOpen(false)} className={`p-2 rounded-xl ${hoverBg} text-gray-400 hover:text-white transition-all`}><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar text-sm leading-relaxed relative">
          {isChatLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-purple-500 gap-5"><div className="relative"><div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl animate-pulse"></div><Loader2 className="h-10 w-10 animate-spin relative z-10" /></div><p className="text-gray-400 text-xs font-bold uppercase tracking-widest animate-pulse">Analyzing Payload...</p></div>
          ) : chatResult ? (
            <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
              {chatResult.analysis && <div className="bg-[#11131a] border border-gray-800 p-5 rounded-2xl whitespace-pre-wrap text-gray-300 shadow-inner">{chatResult.analysis}</div>}
              {chatResult.proposedType === "visualizer" && chatResult.proposedTemplate && (
                <div className="border border-purple-500/30 bg-purple-500/5 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-purple-300 text-xs font-bold uppercase tracking-wide"><Sparkles size={14} /> Proposed Visualization Script</div>
                  <pre className="text-[11px] font-mono bg-black/30 rounded-xl p-3 max-h-48 overflow-auto text-gray-400 whitespace-pre-wrap">{chatResult.proposedTemplate}</pre>
                  <div className="flex gap-2">
                    <button onClick={handleApproveProposal} className="flex-1 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold py-2.5 rounded-lg transition-colors">Approve</button>
                    <button onClick={handleRejectProposal} className="flex-1 border border-gray-700 text-gray-300 text-xs font-bold py-2.5 rounded-lg hover:bg-white/5 transition-colors">Reject</button>
                  </div>
                </div>
              )}
              {chatResult.proposedType === "requestPatch" && chatResult.proposedPatch && (
                <div className="border border-green-500/30 bg-green-500/5 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-green-300 text-xs font-bold uppercase tracking-wide"><Wand2 size={14} /> Proposed Fix</div>
                  {(chatResult.proposedPatch.changes || []).length > 0 && (
                    <ul className="text-xs text-gray-300 space-y-1.5 list-disc list-inside">
                      {chatResult.proposedPatch.changes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  )}
                  <div className="flex gap-2">
                    <button onClick={handleApproveProposal} className="flex-1 bg-green-600 hover:bg-green-500 text-white text-xs font-bold py-2.5 rounded-lg transition-colors">Approve &amp; Resend</button>
                    <button onClick={handleRejectProposal} className="flex-1 border border-gray-700 text-gray-300 text-xs font-bold py-2.5 rounded-lg hover:bg-white/5 transition-colors">Reject</button>
                  </div>
                </div>
              )}
              {chatResult.suggestions && chatResult.suggestions.length > 0 && (
                <div className="space-y-2">
                  {chatResult.suggestions.map((s, i) => (
                    <div key={i} className="border border-gray-800 rounded-xl p-3 flex items-start justify-between gap-3 hover:border-yellow-500/40 transition-colors">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${darkMode ? "bg-white/5" : "bg-white border"} ${getMethodColor(s.method)}`}>{s.method || "GET"}</span><span className="text-sm font-bold text-gray-200 truncate">{s.title}</span></div>
                        {s.description && <p className="text-xs text-gray-500 mt-1">{s.description}</p>}
                        {s.url && <p className="text-[11px] text-gray-600 font-mono mt-1 truncate">{s.url}</p>}
                      </div>
                      <button onClick={() => handleTrySuggestion(s)} className="shrink-0 text-xs font-bold text-yellow-400 hover:text-yellow-300 bg-yellow-500/10 px-2.5 py-1.5 rounded-lg transition-colors">Try</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <div className="w-16 h-16 bg-gray-800/30 rounded-2xl flex items-center justify-center mb-4"><MessageSquare className="h-8 w-8 text-gray-500" /></div>
              <h4 className="font-bold text-gray-300 mb-1">Ready to assist</h4><p className="text-gray-500 text-xs">Select an AI action from the Send menu to deeply analyze this request.</p>
            </div>
          )}
        </div>
      </aside>

      <main className={`order-1 min-w-0 flex-1 px-8 pb-8 ${textColor} relative animate-in fade-in duration-500`}>

      {/* HEADER & ENVIRONMENT SELECTOR */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 mt-2 gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">API Testing</h2>
          <p className={`mt-1 text-sm ${textMuted}`}>
            Enterprise sandbox architecture to execute, test, and save your endpoint validations
          </p>
        </div>
        
        {/* Postman-Style Environment Selector */}
        <div className="flex items-center gap-2 bg-transparent">
          <div className={`flex items-center gap-2 rounded-xl px-3 py-1.5 border ${borderColor} ${inputBg} shadow-sm transition-colors focus-within:border-purple-500/50`}>
            <Globe size={16} className={activeEnvironmentId === 'none' ? textMuted : 'text-purple-400'} />
            <select
              value={activeEnvironmentId}
              onChange={(e) => setActiveEnvironmentId(e.target.value)}
              className={`bg-transparent text-sm font-semibold outline-none w-36 truncate cursor-pointer ${activeEnvironmentId === 'none' ? textMuted : textColor}`}
            >
              <option value="none" className="text-gray-900">No Environment</option>
              {environments.map(env => (
                <option key={env.id} value={env.id} className="text-gray-900">{env.name}</option>
              ))}
            </select>
          </div>
          {!isProjectReadOnly && (
            <button
              onClick={() => {
                if (activeEnvironmentId !== 'none') setEditingEnvId(activeEnvironmentId);
                else if (environments.length > 0) setEditingEnvId(environments[0].id);
                setShowEnvManager(true);
              }}
              className={`p-2 rounded-xl border ${borderColor} ${inputBg} ${textMuted} hover:text-purple-400 hover:border-purple-500/50 transition-all shadow-sm`}
              title="Environment Quick Look"
            >
              <Settings2 size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div className="w-full space-y-6">
          <div className={`rounded-2xl border transition-colors ${cardBg} ${borderColor} shadow-sm overflow-hidden`}>
            <div className={`flex items-center border-b ${borderColor} ${inputBg} overflow-x-auto`}>
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => setActiveTabId(tab.id)}
                  className={`group flex items-center gap-2 px-5 py-3.5 min-w-[150px] max-w-[220px] border-r ${borderColor} cursor-pointer text-sm transition-all ${activeTabId === tab.id ? `${cardBg} border-t-2 border-t-purple-500 font-medium` : `${textMuted}${hoverBg} border-t-2 border-t-transparent`}`}
                >
                  <span className={`text-[10px] font-bold ${getMethodColor(tab.method)}`}>{tab.method}</span>
                  <span className="truncate flex-1">{tab.name !== "Untitled Request" ? tab.name : tab.url || "Untitled Request"}</span>
                  <button onClick={(e) => handleCloseTab(e, tab.id)} className={`p-1 rounded-md ${hoverBg} ${tabs.length === 1 ? "invisible" : "opacity-0 group-hover:opacity-100"}`}><X size={12} /></button>
                </div>
              ))}
              <button onClick={handleAddTab} className={`p-2 mx-3 ${textMuted} hover:text-purple-500 rounded-lg ${hoverBg}`}><Plus size={16} /></button>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div className="flex flex-col xl:flex-row gap-3 w-full">
                  <div className={`flex flex-1 items-center rounded-xl border ${borderColor} ${inputBg} overflow-hidden transition-all focus-within:ring-1 focus-within:ring-purple-500/50 focus-within:border-purple-500/50`}>
                    <select value={activeTab.method} onChange={(e) => updateActiveTab("method", e.target.value)} className={`w-28 border-0 border-r ${borderColor} bg-transparent px-4 py-3 text-sm font-bold outline-none cursor-pointer ${getMethodColor(activeTab.method)} ${hoverBg}`}>
                      <option className="text-gray-900">GET</option><option className="text-gray-900">POST</option><option className="text-gray-900">PUT</option><option className="text-gray-900">PATCH</option><option className="text-gray-900">DELETE</option>
                    </select>
                    <input value={activeTab.url} onChange={(e) => updateActiveTab("url", e.target.value)} placeholder="Enter request URL or {{baseUrl}}" className="flex-1 border-0 bg-transparent px-5 py-3 outline-none focus:ring-0 text-sm font-mono text-purple-400" />
                  </div>
                  <div className="flex gap-3 shrink-0">
                    {canExecuteRequests && (
                    <div className="flex rounded-xl shadow-lg shadow-purple-500/20 relative">
                      <button onClick={() => handleSendRequest()} disabled={activeTab.isLoading || !activeTab.url} className="rounded-l-xl bg-purple-600 hover:bg-purple-500 px-8 py-3 text-sm font-bold text-white transition disabled:opacity-50 flex items-center gap-2">
                        {activeTab.isLoading ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />} Send
                      </button>
                      <div className="relative border-l border-purple-800">
                        <button onClick={() => setSendDropdownOpen(!sendDropdownOpen)} disabled={activeTab.isLoading || !activeTab.url} className="rounded-r-xl bg-purple-600 hover:bg-purple-500 px-3 py-3 text-white transition h-full flex items-center justify-center disabled:opacity-50"><ChevronDown size={16} /></button>
                        {sendDropdownOpen && <div className="fixed inset-0 z-40" onClick={() => setSendDropdownOpen(false)}></div>}
                        {sendDropdownOpen && (
                          <div className={`absolute top-full right-0 mt-2 w-72 rounded-xl border ${borderColor} ${cardBg} shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200`}>
                            <div className="flex flex-col py-1">
                              <div className="px-4 py-2 border-b border-gray-800/50 mb-1"><span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">AI Tools</span></div>
                              <button onClick={() => handleAIAssistant('success')} className={`w-full text-left px-4 py-3 text-sm transition-colors ${hoverBg} flex items-center gap-3 text-gray-200`}><Sparkles size={16} className="text-purple-400" /> Get a successful response</button>
                              <button onClick={() => { setSendDropdownOpen(false); setResponseViewTab("Visualize"); handleGenerateVisualization(); }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${hoverBg} flex items-center gap-3 text-gray-200`}><Eye size={16} className="text-blue-400" /> Visualize response</button>
                              <button onClick={() => { setSendDropdownOpen(false); setActiveSubTab("Scripts"); handleAIGenerateTests(); }} className={`w-full text-left px-4 py-3 text-sm transition-colors ${hoverBg} flex items-center gap-3 text-gray-200`}><Code size={16} className="text-green-400" /> Write tests</button>
                              <button onClick={() => handleAIAssistant('debug')} className={`w-full text-left px-4 py-3 text-sm transition-colors ${hoverBg} flex items-center gap-3 text-gray-200`}><Bug size={16} className="text-red-400" /> Debug request</button>
                              <button onClick={() => handleAIAssistant('explore')} className={`w-full text-left px-4 py-3 text-sm transition-colors ${hoverBg} flex items-center gap-3 text-gray-200`}><Compass size={16} className="text-yellow-400" /> Explore API capabilities</button>
                              <div className="h-px bg-gray-800/50 my-1"></div>
                              <button onClick={handleDownloadResponse} className={`w-full text-left px-4 py-3 text-sm transition-colors ${hoverBg} flex items-center gap-3 text-gray-400 hover:text-white`}><Download size={16} /> Download response</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                    {canManageCollections && (
                      <button onClick={handleOpenSave} className={`flex items-center gap-2 px-6 py-3 rounded-xl border ${borderColor} text-sm font-bold ${hoverBg} transition text-gray-300 hover:text-white`}><Save size={16} /> Save</button>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <div className={`flex gap-6 border-b ${borderColor} mb-4`}>
                    {["Params", "Auth", "Headers", "Body", "Scripts"].map((sub) => (
                      <button key={sub} onClick={() => setActiveSubTab(sub)} className={`pb-3 text-sm font-bold transition-colors border-b-2 ${activeSubTab === sub ? "border-purple-500 text-purple-500" : `border-transparent ${textMuted} hover:text-gray-300`}`}>{sub}</button>
                    ))}
                  </div>

                  <div className="min-h-[160px]">
                    {activeSubTab === "Params" && renderKeyValueEditor("params")}
                    {activeSubTab === "Headers" && renderKeyValueEditor("headers")}
                    {activeSubTab === "Auth" && renderAuthEditor()}

                    {activeSubTab === "Scripts" && (
                      <div className="space-y-4 animate-in fade-in duration-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-400 flex items-center gap-1"><Code size={12} /> Validation Scripts (Executed upon Send)</span>
                          <button onClick={handleAIGenerateTests} disabled={activeTab.isAILoading} className="flex items-center gap-1.5 text-xs font-bold text-purple-400 hover:text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 px-3 py-1.5 rounded-lg transition-colors">
                            {activeTab.isAILoading ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Generate Tests with AI
                          </button>
                        </div>
                        {activeTab.aiProposedTests && activeTab.aiProposedTests.length > 0 && (
                          <div className="p-4 rounded-xl border border-purple-500/30 bg-purple-500/5 space-y-3 shadow-inner">
                            <div className="flex items-center justify-between mb-3 border-b border-purple-500/20 pb-2">
                              <span className="text-sm font-bold text-purple-400 flex items-center gap-2"><Info size={16} /> Review AI Test Suggestions</span>
                              <button onClick={() => updateActiveTab("aiProposedTests", [])} className="text-xs font-bold text-gray-500 hover:text-gray-300 flex items-center gap-1"><X size={12} /> Dismiss</button>
                            </div>
                            <div className="grid gap-2">
                              {activeTab.aiProposedTests.map((test, idx) => (
                                <div key={idx} className={`p-3 rounded-xl border ${borderColor} ${cardBg} flex items-center justify-between gap-4 group hover:border-purple-500/50 transition-colors`}>
                                  <div><p className="text-sm font-bold text-gray-200">{test.title}</p><p className="text-xs text-gray-500 mt-0.5">{test.description}</p></div>
                                  <button onClick={() => { updateActiveTab("testScript", activeTab.testScript + "\n\n// 🤖 AI Validation: " + test.title + "\n" + test.code); updateActiveTab("aiProposedTests", activeTab.aiProposedTests.filter((_, i) => i !== idx)); }} className="shrink-0 flex items-center gap-1.5 text-xs font-bold bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded-lg transition-colors"><Plus size={14} /> Add to script</button>
                                </div>
                              ))}
                            </div>
                            {activeTab.aiProposedTests.length > 1 && (
                              <button onClick={() => { updateActiveTab("testScript", activeTab.testScript + "\n\n" + activeTab.aiProposedTests.map((t) => "// 🤖 AI Validation: " + t.title + "\n" + t.code).join("\n\n")); updateActiveTab("aiProposedTests", []); }} className="text-xs font-bold text-purple-300 hover:text-purple-200 mt-2 w-full text-center py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors">Add All {activeTab.aiProposedTests.length} Suggestions</button>
                            )}
                          </div>
                        )}
                        <textarea rows={8} value={activeTab.testScript} onChange={(e) => updateActiveTab("testScript", e.target.value)} placeholder="// Write your JavaScript tests here or generate them with AI..." className={`w-full rounded-xl border ${borderColor} ${inputBg} px-4 py-3 font-mono text-sm outline-none transition-all text-green-400 focus:border-purple-500/50`} />
                      </div>
                    )}

                    {activeSubTab === "Body" && (
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-5 text-sm items-center border-b border-transparent pb-2 justify-between">
                          <div className="flex flex-wrap gap-5">
                            {["none", "form-data", "x-www-form-urlencoded", "raw", "binary", "GraphQL"].map((type) => (
                              <label key={type} className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
                                <input type="radio" checked={activeTab.bodyType === type} onChange={() => updateActiveTab("bodyType", type)} disabled={["GET", "DELETE"].includes(activeTab.method)} className="accent-purple-500 cursor-pointer disabled:cursor-not-allowed" />
                                <span className={`${activeTab.bodyType === type ? textColor : textMuted} ${["GET", "DELETE"].includes(activeTab.method) ? "opacity-50" : ""}`}>{type}</span>
                              </label>
                            ))}
                            {activeTab.bodyType === "raw" && (
                              <select value={activeTab.rawBodyType} onChange={(e) => updateActiveTab("rawBodyType", e.target.value)} className={`ml-1 bg-transparent border-none text-purple-500 font-bold text-sm outline-none cursor-pointer ${hoverBg} rounded px-1 disabled:opacity-50`} disabled={["GET", "DELETE"].includes(activeTab.method)}>
                                <option className="text-gray-900" value="Text">Text</option><option className="text-gray-900" value="JavaScript">JavaScript</option><option className="text-gray-900" value="JSON">JSON</option><option className="text-gray-900" value="HTML">HTML</option><option className="text-gray-900" value="XML">XML</option>
                              </select>
                            )}
                          </div>
                          {!["GET", "DELETE"].includes(activeTab.method) && (
                            <button onClick={handleAIGenerateMockBody} disabled={activeTab.isAILoading || !activeTab.url} className="flex items-center gap-1.5 text-xs font-bold text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 px-3 py-1.5 rounded-lg transition-colors">
                              {activeTab.isAILoading ? <Loader2 className="animate-spin" size={14} /> : <Wand2 size={14} />} AI Mock Payload
                            </button>
                          )}
                        </div>
                        {["GET", "DELETE"].includes(activeTab.method) ? (
                          <div className={`p-4 text-center text-sm ${textMuted}`}>HTTP payload definitions are blocked for GET/DELETE operations.</div>
                        ) : (
                          <div className="animate-in fade-in duration-200">
                            {activeTab.bodyType === "none" && (<div className={`p-4 text-center text-sm ${textMuted}`}>This request does not have a body payload.</div>)}
                            {activeTab.bodyType === "form-data" && renderKeyValueEditor("formData")}
                            {activeTab.bodyType === "x-www-form-urlencoded" && renderKeyValueEditor("urlEncoded")}
                            {activeTab.bodyType === "raw" && (<textarea rows={6} value={activeTab.body} onChange={(e) => updateActiveTab("body", e.target.value)} placeholder={activeTab.rawBodyType === "JSON" ? '{\n  "key": "value"\n}' : "Enter body data here..."} className={`w-full rounded-xl border ${borderColor} ${inputBg} px-4 py-3 font-mono text-sm outline-none transition-all`} />)}
                            {activeTab.bodyType === "binary" && (
                              <div className={`border-2 border-dashed ${borderColor} rounded-xl p-8 text-center bg-gray-50 dark:bg-black/20`}>
                                <input type="file" onChange={(e) => updateActiveTab("binaryFile", e.target.files[0])} className={`text-sm ${textMuted} file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-purple-600 file:text-white hover:file:bg-purple-700 transition-all cursor-pointer outline-none`} />
                                {activeTab.binaryFile && <p className="mt-3 text-sm font-bold text-purple-500">Target Selected: {activeTab.binaryFile.name}</p>}
                              </div>
                            )}
                            {activeTab.bodyType === "GraphQL" && (<textarea rows={6} value={activeTab.graphqlQuery} onChange={(e) => updateActiveTab("graphqlQuery", e.target.value)} placeholder="query { user { id name } }" className={`w-full rounded-xl border ${borderColor} ${inputBg} px-4 py-3 font-mono text-sm outline-none transition-all`} />)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {activeTab.response && (
            <div className={`rounded-2xl border ${borderColor} ${cardBg} p-6 shadow-sm`}>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-4 border-b border-gray-800 pb-4">
                <div className="flex gap-4">
                  {["Pretty", "Raw", "Preview", "Visualize", "Headers", "Test Results"].map((mode) => (
                    <button key={mode} onClick={() => mode === "Visualize" ? handleVisualizeTabClick() : setResponseViewTab(mode)} className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${responseViewTab === mode ? "bg-purple-600 text-white" : `${textMuted}${hoverBg}`}`}>
                      {mode === "Visualize" && <Sparkles size={14} className={responseViewTab === mode ? "text-white" : "text-purple-500"} />} {mode}
                    </button>
                  ))}
                </div>
                <div className={`flex flex-wrap gap-4 text-xs font-medium ${inputBg} px-4 py-2.5 rounded-xl border ${borderColor}`}>
                  <span className={activeTab.response.status >= 200 && activeTab.response.status < 300 ? "text-green-500" : "text-red-500"}>Status: {activeTab.response.status} {activeTab.response.statusText}</span>
                  <span className={`border-l ${borderColor} pl-4`}><span className={textMuted}>Time:</span> {activeTab.response.time}</span>
                  <span className={`border-l ${borderColor} pl-4`}><span className={textMuted}>Size:</span> {activeTab.response.size}</span>
                </div>
              </div>
              <div className={`rounded-xl border ${borderColor} ${inputBg} p-4 text-sm font-mono overflow-auto max-h-[800px]`}>
                {responseViewTab === "Pretty" && <pre className={darkMode ? "text-green-400" : "text-gray-800"}>{typeof activeTab.response.body === "object" ? JSON.stringify(activeTab.response.body, null, 2) : activeTab.response.rawBody}</pre>}
                {responseViewTab === "Raw" && <pre className="text-gray-400 whitespace-pre-wrap">{activeTab.response.rawBody}</pre>}
                {responseViewTab === "Preview" && <iframe title="Response Preview" srcDoc={activeTab.response.rawBody} className="w-full h-64 bg-white rounded-lg border-0" sandbox="allow-scripts" />}
                {responseViewTab === "Visualize" && (
                  <div className="w-full min-h-[600px]">
                    {activeTab.isVisualLoading ? (
                      <div className="flex flex-col items-center justify-center h-[600px] text-purple-500 gap-4"><Loader2 className="h-8 w-8 animate-spin" /><p className="text-gray-400 text-xs font-bold uppercase tracking-wider animate-pulse">Generating Visualization...</p></div>
                    ) : activeTab.visualHtml ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <p className={`text-xs ${textMuted} flex-1 min-w-[200px]`}>{activeTab.visualExplanation}</p>
                          <div className="flex gap-2 shrink-0">
                            <button onClick={() => setShowTemplateEditor((v) => !v)} className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${hoverBg} ${textMuted}`}><Code size={14} /> {showTemplateEditor ? "Hide" : "Edit"} Template</button>
                            <button onClick={handleGenerateVisualization} className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 transition-colors"><RefreshCw size={14} /> Regenerate</button>
                          </div>
                        </div>
                        {showTemplateEditor && (
                          <div className="space-y-2">
                            <textarea rows={8} value={activeTab.visualizerTemplate || ""} onChange={(e) => updateActiveTab("visualizerTemplate", e.target.value)} className={`w-full rounded-xl border ${borderColor} ${inputBg} px-4 py-3 font-mono text-xs outline-none text-purple-300`} />
                            <button onClick={() => applyVisualizerTemplate(activeTab.visualizerTemplate)} className="text-xs font-bold text-white bg-purple-600 hover:bg-purple-500 px-4 py-2 rounded-lg transition-colors">Re-render</button>
                          </div>
                        )}
                        <iframe title="AI Visualization" srcDoc={activeTab.visualHtml} className="w-full h-[600px] bg-white rounded-lg border border-gray-800" sandbox="allow-scripts" />
                      </div>
                    ) : (
                      <div className="text-center py-12 space-y-4">
                        <p className={`${textMuted} text-sm`}>No visualization yet for this response.</p>
                        <button onClick={handleGenerateVisualization} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-purple-600 hover:bg-purple-500 px-5 py-2.5 rounded-xl transition-colors"><Sparkles size={16} /> Generate Visualization</button>
                      </div>
                    )}
                  </div>
                )}
                {responseViewTab === "Headers" && (
                  <div className="space-y-2 font-sans">
                    {activeTab.response.headers.map((h, i) => (
                      <div key={i} className="flex border-b border-gray-800/40 pb-1.5 text-xs"><span className="w-1/3 font-bold text-purple-400 truncate">{h.key}</span><span className="w-2/3 text-gray-300 break-all">{h.value}</span></div>
                    ))}
                  </div>
                )}
                {responseViewTab === "Test Results" && (
                  <div className="space-y-2 font-sans">
                    {(!activeTab.response.testResults || activeTab.response.testResults.length === 0) && (<p className="text-gray-500 text-xs italic">No tests were executed. Run a script to see results.</p>)}
                    {activeTab.response.testResults?.map((tr, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-xl border ${tr.status === "PASS" ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                        <div className="flex flex-col"><span className={`text-sm font-bold ${tr.status === "PASS" ? "text-green-400" : "text-red-400"}`}>{tr.name}</span>{tr.status === "FAIL" && tr.error && <span className="text-xs text-red-300/80 mt-1 bg-red-500/10 px-2 py-1 rounded inline-block max-w-xl">{tr.error}</span>}</div>
                        <span className={`shrink-0 text-xs font-black px-2 py-1 rounded ${tr.status === "PASS" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>{tr.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="w-full">
          <div className={`rounded-2xl border ${borderColor} ${cardBg} p-6 shadow-sm`}>
            <div className="flex items-center justify-between mb-6 border-b border-gray-800/50 pb-4">
              <h3 className="font-bold text-lg">Collections Repository</h3>
              <span className={`text-xs font-bold px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 ${textMuted}`}>{savedRequests.length} Saved Requests</span>
            </div>
            {savedRequests.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-gray-800 rounded-xl">
                <FileCode className={`h-12 w-12 mx-auto mb-4 opacity-20 ${textColor}`} />
                <p className={`text-sm ${textMuted}`}>No collections items preserved. Save a request to build your repository.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-y-auto max-h-[400px] pr-2 custom-scrollbar">
                {savedRequests.map((req, i) => (
                  <div key={req._id || i} onClick={() => {
                      updateActiveTab("name", req.name); updateActiveTab("method", req.method); updateActiveTab("url", req.url);
                      updateActiveTab("bodyType", req.bodyType || "raw"); updateActiveTab("rawBodyType", req.rawBodyType || "JSON"); updateActiveTab("body", req.body || "");
                      updateActiveTab("formData", req.formData || [{ key: "", value: "" }]); updateActiveTab("urlEncoded", req.urlEncoded || [{ key: "", value: "" }]); updateActiveTab("graphqlQuery", req.graphqlQuery || "");
                      updateActiveTab("params", req.params || [{ key: "", value: "" }]); updateActiveTab("headers", req.headers || [{ key: "", value: "" }]);
                      
                      const cleanAuth = { ...req.auth };
                      if (cleanAuth._meta) delete cleanAuth._meta;
                      updateActiveTab("testScript", req.testScript || ""); updateActiveTab("auth", cleanAuth || { type: "none" }); updateActiveTab("response", null);
                      
                      updateActiveTab("visualizerTemplate", null); updateActiveTab("visualHtml", null); updateActiveTab("visualExplanation", "");
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`border ${borderColor} p-4 rounded-xl ${inputBg} hover:border-purple-500 cursor-pointer group flex flex-col justify-between transition-colors h-[110px]`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex gap-3 items-center overflow-hidden pr-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0 ${darkMode ? "bg-white/5" : "bg-white border"} ${getMethodColor(req.method)}`}>{req.method}</span>
                        <span className="text-sm font-bold truncate group-hover:text-purple-500 transition-colors">{req.name}</span>
                      </div>
                      {canManageCollections && (
                        <button onClick={(e) => handleDelete(e, req._id)} className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded shrink-0 transition-all"><Trash2 size={16} /></button>
                      )}
                    </div>
                    <p className={`text-xs ${textMuted} truncate mt-1 font-mono`}>{req.url}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </main>

      {/* SAVE REQUEST MODAL */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className={`${cardBg} p-6 rounded-2xl w-full max-w-md shadow-2xl border ${borderColor}`}>
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-bold text-lg">Commit Request to Archive</h3>
              <button onClick={() => setShowSaveModal(false)} className={`p-1 rounded-md ${hoverBg}`}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1.5">Request Name</label>
                <input placeholder="e.g., Get Core Organization Profiles" value={saveData.name} onChange={(e) => setSaveData({ ...saveData, name: e.target.value })} className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} focus:ring-1 focus:ring-purple-500 outline-none`} />
              </div>
              <div className="flex gap-4">
                <div className="w-1/3"><label className="block text-sm font-bold mb-1.5">Method</label><input disabled value={saveData.method} className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} opacity-70 cursor-not-allowed font-bold ${getMethodColor(saveData.method)}`} /></div>
                <div className="w-2/3"><label className="block text-sm font-bold mb-1.5">URL</label><input value={saveData.url} onChange={(e) => setSaveData({ ...saveData, url: e.target.value })} className={`w-full p-2.5 text-sm border ${borderColor} rounded-xl ${inputBg} outline-none`} /></div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setShowSaveModal(false)} className={`px-5 py-2.5 text-sm font-bold border ${borderColor} rounded-xl ${hoverBg} transition`}>Cancel</button>
              {canManageCollections && (
                <button onClick={handleSaveRequest} disabled={!saveData.name || !saveData.url} className="px-5 py-2.5 text-sm font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition disabled:opacity-50 shadow-lg shadow-purple-500/20">Save to Collection</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ENVIRONMENT MANAGER MODAL */}
      {showEnvManager && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className={`${cardBg} rounded-2xl w-full max-w-5xl shadow-2xl border ${borderColor} flex flex-col h-[80vh] overflow-hidden`}>
            <div className={`p-5 border-b ${borderColor} flex justify-between items-center bg-gradient-to-r from-gray-500/10 to-transparent`}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-500/20 rounded-lg"><Layers className="h-5 w-5 text-gray-400" /></div>
                <div>
                  <h3 className="font-bold text-base tracking-tight">Manage Environments</h3>
                  <p className={`text-[11px] font-medium ${textMuted} uppercase tracking-widest`}>Configure variables like {'{{baseUrl}}'}</p>
                </div>
              </div>
              <button onClick={() => setShowEnvManager(false)} className={`p-2 rounded-xl ${hoverBg} ${textMuted} hover:text-white transition-all`}><X size={18} /></button>
            </div>
            
            <div className="flex flex-1 overflow-hidden">
              {/* Left Sidebar: Environment List */}
              <div className={`w-1/3 border-r ${borderColor} flex flex-col`}>
                <div className="p-4 border-b border-gray-800/50">
                  <button onClick={handleCreateEnvironment} className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold py-2.5 rounded-lg transition-colors">
                    <Plus size={14} /> New Environment
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2 custom-scrollbar space-y-1">
                  {environments.length === 0 ? (
                    <div className={`p-4 text-center text-xs ${textMuted}`}>No environments exist.</div>
                  ) : environments.map(env => (
                    <div 
                      key={env.id} 
                      onClick={() => setEditingEnvId(env.id)}
                      className={`flex justify-between items-center p-3 rounded-xl cursor-pointer transition-colors ${editingEnvId === env.id ? 'bg-purple-500/10 border border-purple-500/30 text-purple-400 font-bold' : `hover:bg-gray-800/40 text-gray-300 border border-transparent`}`}
                    >
                      <span className="truncate pr-2 text-sm">{env.name}</span>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteEnvironment(env.id); }} className="text-gray-500 hover:text-red-500 p-1 rounded hover:bg-red-500/10 transition-colors"><Trash2 size={14}/></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Content: Variable Editor */}
              <div className={`w-2/3 flex flex-col ${inputBg}`}>
                {editingEnvId && environments.find(e => e.id === editingEnvId) ? (
                  <>
                    <div className={`p-5 border-b ${borderColor} flex items-center`}>
                      <input 
                        value={environments.find(e => e.id === editingEnvId).name} 
                        onChange={(e) => {
                          setEnvironments(envs => envs.map(env => env.id === editingEnvId ? { ...env, name: e.target.value } : env));
                        }}
                        className={`bg-transparent text-lg font-bold outline-none border-b border-dashed ${borderColor} focus:border-purple-500 w-full px-2 py-1 text-white`}
                        placeholder="Environment Name"
                      />
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-2">
                      <div className="flex gap-2 mb-3 px-2 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-800 pb-2">
                        <div className="flex-1">Variable</div>
                        <div className="flex-1">Initial Value</div>
                        <div className="w-8"></div>
                      </div>
                      {environments.find(e => e.id === editingEnvId).variables.map((item, i) => (
                        <div key={i} className="flex gap-2 items-center group">
                          <input 
                            value={item.key} 
                            onChange={(e) => updateEnvKeyValue(editingEnvId, i, "key", e.target.value)} 
                            placeholder="e.g., baseUrl" 
                            className={`flex-1 p-2.5 text-sm border ${borderColor} rounded-lg bg-[#161922] focus:ring-1 focus:ring-purple-500 outline-none font-mono text-purple-300`} 
                          />
                          <input 
                            value={item.value} 
                            onChange={(e) => updateEnvKeyValue(editingEnvId, i, "value", e.target.value)} 
                            placeholder="Value" 
                            className={`flex-1 p-2.5 text-sm border ${borderColor} rounded-lg bg-[#161922] focus:ring-1 focus:ring-purple-500 outline-none text-gray-300`} 
                          />
                          <button onClick={() => removeEnvRow(editingEnvId, i)} className={`p-2 text-gray-500 hover:text-red-500 transition-colors ${i === environments.find(e => e.id === editingEnvId).variables.length - 1 && !item.key ? 'invisible' : 'opacity-0 group-hover:opacity-100'}`}><X size={16} /></button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className={`flex items-center justify-center h-full text-sm ${textMuted}`}>Select an environment to edit its variables.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}